import { Component, forceUpdate, h, Host, Prop, State } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import { formatDate } from "../utils";

@Component({
    tag: "ux-chat-item",
    styleUrl: "chat-item.scss",
    shadow: true
})
export class ChatItem {
    /**
     * The name of the user who sent the chat message.
     * @required
     */
    @Prop() readonly name!: string;

    /**
     * The date when the chat message was sent.
     */
    @Prop() readonly date!: string;

    /**
     * Indicates whether the chat message was sent by the current user system or bot
     */
    @Prop({ reflect: true }) readonly source!: "user" | "system" | "bot";

    /**
     * Optional URL for the profile picture. If not provided, the default icon will be shown for bot messages.
     */
    @Prop() readonly profilePicture?: string;

    /**
     * State to track the current locale
     */
    @State() locale: string;

    /**
     * State to store the formatted date string
     */
    @State() private formattedDate: string | null = null;

    componentWillLoad() {
        // Subscribe to i18nProvider updates and update locale
        this.updateLocale();
        i18nProvider.subscribe(() => {
            this.updateLocale();
            this.updateFormattedDate();
            forceUpdate(this);
        });

        // Initial date formatting
        this.updateFormattedDate();
    }

    private updateLocale() {
        const { language } = i18nProvider;

        const defaultRegionMap: Record<string, string> = {
            en: "en-US",
            de: "de-DE"
        };

        this.locale = defaultRegionMap[language] || `${language}-${language.toUpperCase()}`;
    }

    /**
     * Updates the formatted date string
     */
    private updateFormattedDate(): void {
        this.formattedDate = this.date ? formatDate(this.date, this.locale) : null;
    }

    render() {
        return (
            <Host role="listitem">
                <div id="chat-item-container">
                    {this.source === "bot" && (
                        <div class="profile-picture">
                            {this.profilePicture ? (
                                <img src={this.profilePicture} alt={`${this.name} profile picture`} />
                            ) : (
                                <ux-icon name="ai-assistent" size-s="35" weight="normal" fill="white"></ux-icon>
                            )}
                        </div>
                    )}
                    <div>
                        <div class="message-content">
                            <slot></slot>
                        </div>
                        {(this.name || this.formattedDate) && (
                            <div class={`message-meta ${this.source === "user" ? "user-message-footer" : ""}`}>
                                <ux-copy class="chat-name" variant="tiny">
                                    {[this.name, this.formattedDate].filter(Boolean).join(" - ")}
                                </ux-copy>
                            </div>
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
